import { useMemo, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Form from "../../inputs/Form";
import { isValidEmail } from "../../../utils/formatting";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faTimes,
  faExclamationTriangle,
} from "@fortawesome/pro-regular-svg-icons";
import useEmailValidation from "../../../hooks/useEmailValidation";
import useLangNavigate from "../../../hooks/useLangNavigate";
import Button from "../../buttons/Button";
import { verifyPassword } from "../../../services/authService";
import { toast } from "react-toastify";
import Alert from "../../utils/Alert";

function StepRestaurantCredentials({
  currentForm = {},
  setCredentialsDetails = () => {},
  setIsValid = () => {},
}) {
  const { t } = useTranslation();
  const navigate = useLangNavigate();
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const email = currentForm?.email || "";
  const {
    status: emailStatus,
    isLoading: emailLoading,
    error: emailError,
  } = useEmailValidation(email, "restaurant");

  // Reset password verification when email changes
  useEffect(() => {
    setIsPasswordVerified(false);
  }, [email]);

  useEffect(() => {
    if (emailStatus === "exists_with_role") {
      setIsValid(false);
    } else if (emailStatus === "exists_no_role") {
      // For existing users, form is only valid if password is verified
      const password = currentForm?.password || "";
      if (password && isPasswordVerified) {
        setIsValid(true);
      } else {
        setIsValid(false);
      }
    }
  }, [emailStatus, isPasswordVerified, currentForm?.password, setIsValid]);

  // Password constraints
  const password = currentForm.password || "";
  const checks = useMemo(
    () => ({
      length: password.length >= 12,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_\-+=[\]{}<>\\|/?.,:;"'~`€£¥₹]/.test(password),
    }),
    [password]
  );

  const fields = useMemo(
    () => [
      {
        name: "email",
        label: t("register.step6.email.label"),
        placeholder: t("register.step6.email.placeholder"),
        type: "email",
        class: "w-100",
        validate: (value) => {
          if (!value) return t("register.errors.email.required");
          if (!isValidEmail(value)) return t("register.errors.email.invalid");
          return "";
        },
        afterField:
          email &&
          emailStatus !== "idle" &&
          emailStatus !== "available" &&
          !emailLoading ? (
            <div className="mt-2">
              {emailStatus === "exists_no_role" && (
                <Alert variant="pastel-warning" className="mt-2">
                  <div className="flex align-center">
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="mr-2"
                    />
                    <span>{t("register.errors.email.exists_will_link")}</span>
                  </div>
                </Alert>
              )}
              {emailStatus === "exists_with_role" && (
                <Alert variant="pastel-danger" className="mt-2">
                  <div className="flex column gap-1">
                    <div>{t("register.errors.email.exists_with_role")}</div>
                    <div>
                      <Button size="sm" onClick={() => navigate("login")}>
                        {t("register.loginInstead")}
                      </Button>
                    </div>
                  </div>
                </Alert>
              )}
              {emailStatus === "error" && emailError && (
                <div className="flex align-center text-danger">
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  <span>{emailError}</span>
                </div>
              )}
            </div>
          ) : null,
      },
      {
        name: "password",
        label: t("register.step6.password.label"),
        placeholder: t("register.step6.password.placeholder"),
        type: "password",
        class: "w-100",
        validate: (value) => {
          // Password is required for existing users
          if (emailStatus === "exists_no_role" && !value) {
            return t("register.errors.password.verification_required");
          }
          if (!value) return t("register.errors.password.required");
          if (emailStatus === "exists_no_role") return "";
          if (
            !checks.length ||
            !checks.lowercase ||
            !checks.uppercase ||
            !checks.number ||
            !checks.special
          )
            return t("register.errors.password.invalid");
          return "";
        },
        info:
          emailStatus === "exists_no_role"
            ? t("register.step6.password.infoExisting")
            : t("register.step6.password.info"),
      },
      // Only show confirm password for new users (not existing accounts)
      ...(emailStatus !== "exists_no_role"
        ? [
            {
              name: "confirmPassword",
              label: t("register.step6.confirmPassword.label"),
              placeholder: t("register.step6.confirmPassword.placeholder"),
              type: "password",
              class: "w-100",
              autoComplete: "off",
              validate: (value, form) => {
                if (!value)
                  return t("register.errors.confirmPassword.required");
                if (!form || value !== form.password)
                  return t("register.errors.confirmPassword.invalid");
                return "";
              },
            },
          ]
        : []),
    ],
    [
      email,
      emailStatus,
      emailLoading,
      emailError,
      t,
      navigate,
      checks.length,
      checks.lowercase,
      checks.uppercase,
      checks.number,
      checks.special,
    ]
  );

  // Verify password for existing users
  const verifyPasswordForExistingUser = useCallback(
    async (email, password) => {
      if (!email || !password) {
        setIsPasswordVerified(false);
        return false;
      }

      setIsVerifyingPassword(true);

      try {
        const response = await verifyPassword(email, password);
        if (response.data?.valid) {
          setIsPasswordVerified(true);
          return true;
        } else {
          setIsPasswordVerified(false);
          toast.error(t("register.errors.password.incorrect"));
          return false;
        }
      } catch (error) {
        setIsPasswordVerified(false);
        let errorMessage;

        const statusCode = error.status || error.response?.status;
        const errorCode =
          error.response?.data?.code ||
          error.response?.data?.message ||
          error.message;

        if (statusCode === 401) {
          errorMessage = t("register.errors.password.incorrect");
        } else if (
          errorCode === "INVALID_PASSWORD" ||
          errorCode === "INVALID_CREDENTIALS"
        ) {
          errorMessage = t("register.errors.password.incorrect");
        } else if (statusCode === 404 || errorCode === "USER_NOT_FOUND") {
          errorMessage = t("register.errors.password.userNotFound");
        } else if (
          statusCode === 409 ||
          errorCode === "USER_ALREADY_HAS_ADMIN_ROLE"
        ) {
          errorMessage = t("register.errors.password.alreadyHasRole");
        } else if (!statusCode && !error.response) {
          errorMessage = t("register.errors.password.networkError");
        } else {
          errorMessage = t("register.errors.password.verificationError");
        }

        toast.error(errorMessage);
        return false;
      } finally {
        setIsVerifyingPassword(false);
      }
    },
    [t, setIsPasswordVerified, setIsVerifyingPassword]
  );

  useEffect(() => {
    if (
      emailStatus === "exists_no_role" &&
      currentForm?.password &&
      currentForm?.email
    ) {
      const timeoutId = setTimeout(() => {
        verifyPasswordForExistingUser(currentForm.email, currentForm.password);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [
    currentForm?.password,
    currentForm?.email,
    emailStatus,
    verifyPasswordForExistingUser,
  ]);

  const handleCredentialsDetails = (data, isValid) => {
    // Include email status in form data
    const dataWithEmailStatus = {
      ...data,
      emailStatus,
      isExistingUser: emailStatus === "exists_no_role",
      requiresPasswordVerification: emailStatus === "exists_no_role",
      isPasswordVerified:
        emailStatus === "exists_no_role" ? isPasswordVerified : true,
    };
    setCredentialsDetails(dataWithEmailStatus);
  };

  return (
    <div className="register-step">
      <div className="step-header">
        <h2>{t("register.step6.title")}</h2>
      </div>
      <Form
        fields={fields}
        currentForm={currentForm}
        setFormDetails={handleCredentialsDetails}
        setIsValid={(valid) => {
          if (emailStatus === "exists_no_role" && currentForm?.password) {
            setIsValid(valid && isPasswordVerified);
          } else {
            setIsValid(valid);
          }
        }}
      />
      {emailStatus === "exists_no_role" && isVerifyingPassword && (
        <div className="mt-2 flex align-center text-info">
          <span>{t("register.verifyingPassword")}</span>
        </div>
      )}
      {emailStatus !== "exists_no_role" && (
        <div className="flex column gap-1 text-light p3 mt-2">
          <div className="flex align-center">
            <FontAwesomeIcon
              icon={checks.length ? faCheck : faTimes}
              className={`mr-2 ${checks.length ? "text-success" : "text-danger"}`}
            />
            <span className={checks.length && "text-dark"}>
              {t("register.step6.constraints.length")}
            </span>
          </div>
          <div className="flex align-center">
            <FontAwesomeIcon
              icon={checks.lowercase ? faCheck : faTimes}
              className={`mr-2 ${
                checks.lowercase ? "text-success" : "text-danger"
              }`}
            />
            <span className={checks.lowercase && "text-dark"}>
              {t("register.step6.constraints.lowercase")}
            </span>
          </div>
          <div className="flex align-center">
            <FontAwesomeIcon
              icon={checks.uppercase ? faCheck : faTimes}
              className={`mr-2 ${
                checks.uppercase ? "text-success" : "text-danger"
              }`}
            />
            <span className={checks.uppercase && "text-dark"}>
              {t("register.step6.constraints.uppercase")}
            </span>
          </div>
          <div className="flex align-center">
            <FontAwesomeIcon
              icon={checks.number ? faCheck : faTimes}
              className={`mr-2 ${
                checks.number ? "text-success" : "text-danger"
              }`}
            />
            <span className={checks.number && "text-dark"}>
              {t("register.step6.constraints.number")}
            </span>
          </div>
          <div className="flex align-center">
            <FontAwesomeIcon
              icon={checks.special ? faCheck : faTimes}
              className={`mr-2 ${
                checks.special ? "text-success" : "text-danger"
              }`}
            />
            <span className={checks.special && "text-dark"}>
              {t("register.step6.constraints.special")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default StepRestaurantCredentials;
